// @flow
import React from "react";
import log from "loglevel";
import AlignmentPlugin from "@ckeditor/ckeditor5-alignment/src/alignment";
import BoldPlugin from "@ckeditor/ckeditor5-basic-styles/src/bold";
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import EssentialsPlugin from "@ckeditor/ckeditor5-essentials/src/essentials";
import HeadingPlugin from "@ckeditor/ckeditor5-heading/src/heading";
import ItalicPlugin from "@ckeditor/ckeditor5-basic-styles/src/italic";
import ListPlugin from "@ckeditor/ckeditor5-list/src/list";
import ParagraphPlugin from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import StrikethroughPlugin from "@ckeditor/ckeditor5-basic-styles/src/strikethrough";
import UnderlinePlugin from "@ckeditor/ckeditor5-basic-styles/src/underline";
import SubscriptPlugin from "@ckeditor/ckeditor5-basic-styles/src/subscript";
import SuperscriptPlugin from "@ckeditor/ckeditor5-basic-styles/src/superscript";
import CodePlugin from "@ckeditor/ckeditor5-basic-styles/src/code";
import TablePlugin from "@ckeditor/ckeditor5-table/src/table";
import TableToolbarPlugin from "@ckeditor/ckeditor5-table/src/tabletoolbar";
import { translate } from "sulu-admin-bundle/utils/Translator";
import ExternalLinkPlugin from "sulu-admin-bundle/containers/CKEditor5/plugins/ExternalLinkPlugin";
import InternalLinkPlugin from "sulu-admin-bundle/containers/CKEditor5/plugins/InternalLinkPlugin";
import configRegistry from "sulu-admin-bundle/containers/CKEditor5/registries/configRegistry";
import pluginRegistry from "sulu-admin-bundle/containers/CKEditor5/registries/pluginRegistry";
import type { IObservableValue } from "mobx/lib/mobx";
import type { ElementRef } from "react";
import "sulu-admin-bundle/containers/CKEditor5/ckeditor5.scss";

type Props = {|
  disabled: boolean,
  formats: Array<string>,
  locale?: ?IObservableValue<string>,
  onBlur?: () => void,
  onChange: (value: ?string) => void,
  value: ?string,
|};

/**
 * React component that renders a classic ck-editor.
 *
 * Implementation is based upon the official ck-editor component:
 * https://github.com/ckeditor/ckeditor5-react/blob/089e28eafa64baf273c5e3690b08c1f8ee5ebbe5/src/ckeditor.jsx
 */
export default class CKEditor5Inline extends React.Component<Props> {
  containerRef: ?ElementRef<"div">;
  editorInstance: any;

  static defaultProps = {
    disabled: false,
    value: "",
  };

  constructor(props: Props) {
    super(props);

    this.editorInstance = null;
  }

  setContainerRef = (containerRef: ?ElementRef<"div">) => {
    this.containerRef = containerRef;
  };

  componentDidUpdate() {
    if (this.editorInstance) {
      const { value, disabled } = this.props;

      if (disabled) {
        this.editorInstance.ui.element.classList.add("disabled");
        this.editorInstance.enableReadOnlyMode("disabled");
      } else {
        this.editorInstance.ui.element.classList.remove("disabled");
        this.editorInstance.disableReadOnlyMode("disabled");
      }

      const editorData = this.getEditorData();
      if (editorData !== value && !(value === "" && editorData === undefined)) {
        this.editorInstance.setData(value);
      }
    }
  }

  componentDidMount() {
    const { formats, locale } = this.props;

    const defaultConfig = {
      toolbar: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "subscript",
        "superscript",
        "externalLink",
        "internalLink",
      ],
      sulu: {
        locale: locale && locale.get(),
      },
    };

    ClassicEditor.create(this.containerRef, {
      plugins: [
        AlignmentPlugin,
        BoldPlugin,
        EssentialsPlugin,
        ExternalLinkPlugin,
        HeadingPlugin,
        InternalLinkPlugin,
        ItalicPlugin,
        ListPlugin,
        ParagraphPlugin,
        StrikethroughPlugin,
        UnderlinePlugin,
        SubscriptPlugin,
        SuperscriptPlugin,
        CodePlugin,
        TablePlugin,
        TableToolbarPlugin,
        ...pluginRegistry.plugins,
      ],
      ...configRegistry.configs.reduce((previousConfig, config) => {
        return { ...previousConfig, ...config(previousConfig) };
      }, defaultConfig),
    })
      .then((editor) => {
        this.editorInstance = editor;

        this.editorInstance.setData(this.props.value);

        const { disabled, onBlur, onChange } = this.props;
        const {
          model: { document: modelDocument },
          editing: {
            view: { document: viewDocument },
          },
        } = this.editorInstance;

        if (disabled) {
          this.editorInstance.enableReadOnlyMode("disabled");
          this.editorInstance.ui.element.classList.add("disabled");
        }

        if (onBlur) {
          viewDocument.on("blur", () => {
            onBlur();
          });
        }

        if (onChange) {
          modelDocument.on("change", () => {
            if (modelDocument.differ.getChanges().length > 0) {
              onChange(this.getEditorData());
            }
          });
        }
      })
      .catch((error) => {
        log.error(error);
      });
  }

  componentWillUnmount() {
    if (this.editorInstance) {
      this.editorInstance.destroy().then(() => (this.editorInstance = null));
    }
  }

  getEditorData() {
    const editorData = this.editorInstance.getData();
    return editorData === "" ? undefined : editorData;
  }

  render() {
    return <div ref={this.setContainerRef}></div>;
  }
}
